import React, { useState, useEffect, useRef } from "react";
import { Form } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { MdInfoOutline } from "react-icons/md";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { setIndicators, setMapIndicators } from "store/workspaceSlice";
import { setMapConfig, setBarConfig, setPieConfig, setStackedConfig, setTableConfig, resetAllConfigs } from "store/mapConfigSlice";
import { setUlgMapConfig, resetUlgMapConfig } from "store/ulgmapconfigslice";
import { setVisualizationType, clearVisualizationType, resetVisualizationType, setUseTemproBarChart, setUseUlgMap } from "store/visualizationTypeSlice";
import Select from "react-select";
import axios from "axios";
import { useAutoLoadDefaultWorkspace } from 'components/authentication/signup-login/useAutoLoadDefaultWorkspace';
import { BASE_URL } from "commonConfig/ApiConfig";
// Import all visualization components
import WorkspaceSection from './WorkspaceSection';
import MapVisualization from './visualizations/MapVisualization';
import UlgMapVisualization from './visualizations/ulgMapVisualization';
import BarVisualization from './visualizations/BarVisualization';
import PieVisualization from './visualizations/PieVisualization';
import StackedVisualization from './visualizations/StackedVisualization';
import TableVisualization from './visualizations/TableVisualization';
import WorkspaceSidePanel from "components/cmn_workspace/workspace_button";
import WorkspaceButton from "components/pages/workspacebtn/workspacebtn";
import { setWorkspaceLoaded } from 'store/workspaceSlice';

const MapConfig = ({ indicatorData = [] }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    // Auto load default workspace
    useAutoLoadDefaultWorkspace();

    // Redux selectors - only workspace data
    const reduxIndicators = useSelector(state => state.workspace.selectedIndicators);
    const selectedMapFeatures = useSelector(state => state.workspace.selectedMapFeatures);
    const currentWorkspace = useSelector(state => state.workspace.currentWorkspace);
    const previousWorkspaceId = useRef(currentWorkspace?.id);

    // Local state - visualization type selection
    const [selectedVisualizations, setSelectedVisualizations] = useState({
        "value": "bar",
        "label": "Column/ Bar Chart"
    });
    // Workspace toggle state
    const [showWorkspace, setShowWorkspace] = useState(true);


    // ========== MAP STATES (PERSISTENT) ==========
    const [mapLocalSelectedLocations, setMapLocalSelectedLocations] = useState([]);
    const [mapChartType, setMapChartType] = useState('bubble');
    const [mapColorIndicator, setMapColorIndicator] = useState(null);
    const [mapColorSubIndicators, setMapColorSubIndicators] = useState([]);
    const [mapSelectedColorSubIndicator, setMapSelectedColorSubIndicator] = useState(null);
    const [mapPatternIndicator, setMapPatternIndicator] = useState(null);
    const [mapPatternSubIndicators, setMapPatternSubIndicators] = useState([]);
    const [mapSelectedPatternSubIndicator, setMapSelectedPatternSubIndicator] = useState(null);
    const [mapTypeIndicators, setMapTypeIndicators] = useState([]);
    const [mapTypeSubIndicators, setMapTypeSubIndicators] = useState([]);
    const [mapSelectedTypeSubIndicators, setMapSelectedTypeSubIndicators] = useState({});

    // ========== ULG MAP STATES (PERSISTENT) ==========
    const [ulgMapLocalSelectedLocations, setUlgMapLocalSelectedLocations] = useState([]);
    const [ulgMapChartType, setUlgMapChartType] = useState('bubble');
    const [ulgMapTypeIndicator, setUlgMapTypeIndicator] = useState(null);
    const [ulgMapTypeSubIndicators, setUlgMapTypeSubIndicators] = useState([]);
    const [ulgMapSelectedTypeSubIndicator, setUlgMapSelectedTypeSubIndicator] = useState(null);

    // ========== BAR STATES (PERSISTENT) ==========
    const [barLocalSelectedLocations, setBarLocalSelectedLocations] = useState([]);
    const [barChartType, setBarChartType] = useState("column");
    const [barIndicators, setBarIndicators] = useState([
        { colorIndicator: null, colorSubIndicators: [], selectedColorSubIndicator: null, chartType: null }
    ]);

    // ========== PIE STATES (PERSISTENT) ==========
    const [pieLocalSelectedLocations, setPieLocalSelectedLocations] = useState([]);
    const [pie_chartType, setPie_chartType] = useState('pie');
    const [pieIndicators, setPieIndicators] = useState([
        { colorIndicator: null, colorSubIndicators: [], selectedColorSubIndicator: null }
    ]);

    // ========== STACKED STATES (PERSISTENT) ==========
    const [stackedLocalSelectedLocations, setStackedLocalSelectedLocations] = useState([]);
    const [stacked_chartType, setStacked_chartType] = useState('column');
    const [stackedIndicators, setStackedIndicators] = useState([
        { colorIndicator: null, colorSubIndicators: [], selectedColorSubIndicator: null },
    ]);

    // ========== TABLE STATES (PERSISTENT) ==========
    const [tableLocalSelectedLocations, setTableLocalSelectedLocations] = useState([]);
    const [tableSelectedItems, setTableSelectedItems] = useState([]);

    // New visualization type selector
    const currentVisualizationType = useSelector((state) => state.visualizationType.currentType);
    // Checkbox for temporary bar chart redirection
    const useTemproBarChart = useSelector((state) => state.visualizationType.useTemproBarChart);
    const useUlgMap = useSelector((state) => state.visualizationType.useUlgMap);

    // Visualization options
    const visualizationOptions = [
        { value: "map", label: "Map" },
        { value: "bar", label: "Column/ Bar Chart" },
        { value: "pie", label: "Pie / Doughnut Chart" },
        { value: "stacked", label: "Stacked Chart" },
        { value: "table", label: "Data Table" }
    ];

    // Handle indicators from location state
    const { selectedIndicators = [] } = location.state || {};

    useEffect(() => {
        if (selectedIndicators.length > 0) {
            dispatch(setIndicators(selectedIndicators));
        }
    }, [selectedIndicators, dispatch]);

    useEffect(() => {
        if (selectedMapFeatures.length > 0) {
            dispatch(setMapIndicators(selectedMapFeatures));
        }
    }, [selectedMapFeatures, dispatch]);

    // Reset all states when workspace changes
    useEffect(() => {
        if (previousWorkspaceId.current !== currentWorkspace?.id) {
            // Reset Map states
            setMapLocalSelectedLocations([]);
            setMapChartType('bubble');
            setMapColorIndicator(null);
            setMapColorSubIndicators([]);
            setMapSelectedColorSubIndicator(null);
            setMapPatternIndicator(null);
            setMapPatternSubIndicators([]);
            setMapSelectedPatternSubIndicator(null);
            setMapTypeIndicators([]);
            setMapTypeSubIndicators([]);
            setMapSelectedTypeSubIndicators({});

            // Reset ULG Map states
            setUlgMapLocalSelectedLocations([]);
            setUlgMapChartType('bubble');
            setUlgMapTypeIndicator(null);
            setUlgMapTypeSubIndicators([]);
            setUlgMapSelectedTypeSubIndicator(null);

            // Reset Bar states
            setBarLocalSelectedLocations([]);
            setBarChartType("column");
            setBarIndicators([
                { colorIndicator: null, colorSubIndicators: [], selectedColorSubIndicator: null, chartType: null }
            ]);

            // Reset Pie states
            setPieLocalSelectedLocations([]);
            setPie_chartType('pie');
            setPieIndicators([
                { colorIndicator: null, colorSubIndicators: [], selectedColorSubIndicator: null }
            ]);

            // Reset Stacked states
            setStackedLocalSelectedLocations([]);
            setStacked_chartType('column');
            setStackedIndicators([
                { colorIndicator: null, colorSubIndicators: [], selectedColorSubIndicator: null },
            ]);

            // Reset Table states
            setTableLocalSelectedLocations([]);
            setTableSelectedItems([]);

            // Reset visualization selection
            setSelectedVisualizations(null);

            // Reset Redux store configurations
            dispatch(resetAllConfigs());
            dispatch(resetUlgMapConfig());
            dispatch(resetVisualizationType());
        }

        previousWorkspaceId.current = currentWorkspace?.id;
    }, [currentWorkspace?.id, dispatch]);


    // Simple visualization type restoration from Redux
    useEffect(() => {
        if (currentVisualizationType) {
            const viz = visualizationOptions.find(o => o.value === currentVisualizationType);
            if (viz) {
                setSelectedVisualizations(viz);
            }
        }
    }, [currentVisualizationType]);

    // Handle visualization type selection
    const handleVisualizationTypeChange = (selectedOption) => {
        setSelectedVisualizations(selectedOption);
        if (selectedOption) {
            dispatch(setVisualizationType({ type: selectedOption.value, label: selectedOption.label }));
        } else {
            dispatch(clearVisualizationType());
        }
    };

    // ========== CENTRALIZED SUBMIT HANDLER ==========
    const handleCentralizedSubmit = async (visualizationType, preparedData) => {
        try {
            switch (visualizationType) {
                case "map":
                    await handleMapSubmit(preparedData);
                    break;
                case "ulgmap":
                    await handleUlgMapSubmit(preparedData);
                    break;
                case "bar":
                    await handleBarSubmit(preparedData);
                    break;
                case "pie":
                    await handlePieSubmit(preparedData);
                    break;
                case "stacked":
                    await handleStackedSubmit(preparedData);
                    break;
                case "table":
                    await handleTableSubmit(preparedData);
                    break;
                default:
                    console.error("Unknown visualization type:", visualizationType);
            }
        } catch (error) {
            console.error(`Error submitting ${visualizationType}:`, error);
        }
    };

    // ========== MAP SUBMIT ==========
    const handleMapSubmit = async (data) => {
        const { formattedPayload, payload, reduxPayload } = data;

        try {
            // Save to Redux
            dispatch(setMapConfig(reduxPayload));

            // Navigate
            navigate("/map-visualization", {
                state: {
                    payload: payload,
                    formattedPayload: { ...formattedPayload, fromBack: true },
                    reduxPayload: reduxPayload
                },
            });
        } catch (error) {
            console.error("Map submission error:", error);
        }
    };

    // ========== ULG MAP SUBMIT ==========
    const handleUlgMapSubmit = async (data) => {
        const { formattedPayload, payload, reduxPayload } = data;

        try {
            // Save to Redux
            dispatch(setUlgMapConfig(reduxPayload));

            // Navigate
            navigate("/ulg-map-visualization", {
                state: {
                    payload: payload,
                    formattedPayload: { ...formattedPayload, fromBack: true },
                    reduxPayload: reduxPayload
                },
            });
        } catch (error) {
            console.error("ULG Map submission error:", error);
        }
    };

    // ========== BAR SUBMIT ==========
    const handleBarSubmit = async (data) => {
        const { payload, formattedPayload, reduxPayload } = data;

        try {
            // Save to Redux
            dispatch(setBarConfig(reduxPayload));

            // Navigate
            navigate(useTemproBarChart ? "/temporal-barchart" : "/barChart", {
                state: {
                    formattedPayload: formattedPayload,
                    payload: payload,
                    reduxPayload: reduxPayload
                },
            });
        } catch (error) {
            console.error("Bar submission error:", error);
        }
    };

    // ========== PIE SUBMIT ==========
    const handlePieSubmit = async (data) => {
        const { payload, formattedPayload, reduxPayload } = data;

        try {
            // Save to Redux
            dispatch(setPieConfig(reduxPayload));

            // Navigate
            navigate("/piechart", {
                state: {
                    formattedPayload: formattedPayload,
                    payload: payload,
                    reduxPayload: reduxPayload
                },
            });
        } catch (error) {
            console.error("Pie submission error:", error);
        }
    };

    // ========== STACKED SUBMIT ==========
    const handleStackedSubmit = async (data) => {
        const { payload, formattedPayload, reduxPayload } = data;

        try {
            // Save to Redux
            dispatch(setStackedConfig(reduxPayload));

            // Navigate
            navigate("/stackedcolumnchart", {
                state: {
                    payload: payload,
                    formattedPayload: formattedPayload,
                    reduxPayload: reduxPayload
                },
            });
        } catch (error) {
            console.error("Stacked submission error:", error);
        }
    };

    // ========== TABLE SUBMIT ==========
    const handleTableSubmit = async (data) => {
        const { payload, formattedPayload, reduxPayload } = data;

        try {
            // Save to Redux
            dispatch(setTableConfig(reduxPayload));

            // Navigate
            navigate("/tablechart", {
                state: {
                    payload: payload,
                    formattedPayload: formattedPayload,
                    reduxPayload: reduxPayload
                },
            });
        } catch (error) {
            console.error("Table submission error:", error);
        }
    };

    const handleVisualize = (selectedIndicators) => {
        navigate('/mapconfig', { state: { selectedIndicators, selectedMapFeatures } });
        if (showWorkspace === false) {
            setShowWorkspace(true);
        }
    };

    const handleSaveSuccess = (workspaceData) => {
        alert(`Workspace "${workspaceData.name}" saved successfully!`);

        // Always load the saved workspace as current
        if (workspaceData.id && workspaceData.name) {
            setTimeout(() => {
                dispatch(setWorkspaceLoaded({
                    id: workspaceData.id,
                    name: workspaceData.name,
                    workspace: workspaceData
                }));
            }, 100);
        }
    };


    return (

        <>
            <WorkspaceButton
                showWorkspace={showWorkspace}
                setShowWorkspace={setShowWorkspace}
            />
            <WorkspaceSidePanel showWorkspace={showWorkspace}
                setShowWorkspace={setShowWorkspace}
                onVisualize={handleVisualize}
                onSaveSuccess={handleSaveSuccess} />
            <div className="card mt-4 py-3">
                <div className="container">
                    {location.pathname === "/mapconfig" && (
                        <div className="row mb-3 p-2">
                            <div className="col-12">
                                <h3 className="fw-bold">Analysis Builder</h3>
                                <hr />
                            </div>
                        </div>
                    )}

                    <div className="row">
                        <div className="col-md-12">
                            {/* Visualization Type Selector */}
                        </div>
                        <div className="col-md-3 ">
                            <Form.Group controlId="visualizationSelect">
                                <div className="mb-3">
                                    <Form.Label>Visualization Type</Form.Label>
                                    <Select
                                        options={visualizationOptions}
                                        value={selectedVisualizations}
                                        onChange={handleVisualizationTypeChange}
                                        classNamePrefix="select"
                                        placeholder="Choose visualization"
                                    />
                                </div>

                                {/* ✅ Checkbox for Bar chart */}
                                {selectedVisualizations?.value === "bar" && (
                                    <div className="mt-2 mb-0">
                                        <Form.Check
                                            type="checkbox"
                                            id="useTemproBarChart"
                                            label="Temporal Column / Bar Chart"
                                            checked={useTemproBarChart}
                                            onChange={(e) => dispatch(setUseTemproBarChart(e.target.checked))}
                                        />
                                    </div>
                                )}

                                {/* ✅ Checkbox for Map - ULG Map */}
                                {selectedVisualizations?.value === "map" && (
                                    <div className="mt-2 mb-0 row align-items-center g-2">

                                        {/* Checkbox */}
                                        <div className="col-auto">
                                            <Form.Check
                                                type="checkbox"
                                                id="useUlgMap"
                                                label="ULG Level Data Only"
                                                checked={useUlgMap}
                                                onChange={(e) => dispatch(setUseUlgMap(e.target.checked))}
                                            />
                                        </div>

                                        {/* Info Icon */}
                                        <div className="col-auto">
                                            <OverlayTrigger
                                                placement="right"
                                                overlay={
                                                    <Tooltip id="filter-tooltip">
                                                        Selecting this checkbox will allow visualization of ULG level
                                                        data of the selected level of India.
                                                    </Tooltip>
                                                }
                                            >
                                                <span>
                                                    <MdInfoOutline
                                                        size={20}
                                                        style={{ cursor: "pointer", color: "#000000" }}
                                                    />
                                                </span>
                                            </OverlayTrigger>
                                        </div>

                                    </div>
                                )}

                            </Form.Group>
                        </div>

                        <div className="col-md-12 ">
                            {/* Conditional rendering of visualization components with persistent states */}
                            {selectedVisualizations?.value === "map" && !useUlgMap && (
                                <MapVisualization
                                    indicators={reduxIndicators}
                                    locations={selectedMapFeatures}
                                    onSubmit={(data) => handleCentralizedSubmit("map", data)}
                                    // Pass persistent states as props
                                    localSelectedLocations={mapLocalSelectedLocations}
                                    setLocalSelectedLocations={setMapLocalSelectedLocations}
                                    chartType={mapChartType}
                                    setChartType={setMapChartType}
                                    colorIndicator={mapColorIndicator}
                                    setColorIndicator={setMapColorIndicator}
                                    colorSubIndicators={mapColorSubIndicators}
                                    setColorSubIndicators={setMapColorSubIndicators}
                                    selectedColorSubIndicator={mapSelectedColorSubIndicator}
                                    setSelectedColorSubIndicator={setMapSelectedColorSubIndicator}
                                    patternIndicator={mapPatternIndicator}
                                    setPatternIndicator={setMapPatternIndicator}
                                    patternSubIndicators={mapPatternSubIndicators}
                                    setPatternSubIndicators={setMapPatternSubIndicators}
                                    selectedPatternSubIndicator={mapSelectedPatternSubIndicator}
                                    setSelectedPatternSubIndicator={setMapSelectedPatternSubIndicator}
                                    typeIndicators={mapTypeIndicators}
                                    setTypeIndicators={setMapTypeIndicators}
                                    typeSubIndicators={mapTypeSubIndicators}
                                    setTypeSubIndicators={setMapTypeSubIndicators}
                                    selectedTypeSubIndicators={mapSelectedTypeSubIndicators}
                                    setSelectedTypeSubIndicators={setMapSelectedTypeSubIndicators}
                                />
                            )}

                            {/* ✅ ULG Map Visualization */}
                            {selectedVisualizations?.value === "map" && useUlgMap && (
                                <UlgMapVisualization
                                    indicators={reduxIndicators}
                                    locations={selectedMapFeatures}
                                    onSubmit={(data) => handleCentralizedSubmit("ulgmap", data)}
                                    // Pass persistent states as props
                                    localSelectedLocations={ulgMapLocalSelectedLocations}
                                    setLocalSelectedLocations={setUlgMapLocalSelectedLocations}
                                    chartType={ulgMapChartType}
                                    setChartType={setUlgMapChartType}
                                    typeIndicator={ulgMapTypeIndicator}
                                    setTypeIndicator={setUlgMapTypeIndicator}
                                    typeSubIndicators={ulgMapTypeSubIndicators}
                                    setTypeSubIndicators={setUlgMapTypeSubIndicators}
                                    selectedTypeSubIndicator={ulgMapSelectedTypeSubIndicator}
                                    setSelectedTypeSubIndicator={setUlgMapSelectedTypeSubIndicator}
                                />
                            )}

                            {selectedVisualizations?.value === "bar" && (
                                <BarVisualization
                                    indicators={reduxIndicators}
                                    locations={selectedMapFeatures}
                                    onSubmit={(data) => handleCentralizedSubmit("bar", data)}
                                    // Pass persistent states as props
                                    localSelectedLocations={barLocalSelectedLocations}
                                    setLocalSelectedLocations={setBarLocalSelectedLocations}
                                    useTemproBarChart={useTemproBarChart}
                                    barChartType={barChartType}
                                    setBarChartType={setBarChartType}
                                    barIndicators={barIndicators}
                                    setBarIndicators={setBarIndicators}
                                />
                            )}

                            {selectedVisualizations?.value === "pie" && (
                                <PieVisualization
                                    indicators={reduxIndicators}
                                    locations={selectedMapFeatures}
                                    onSubmit={(data) => handleCentralizedSubmit("pie", data)}
                                    // Pass persistent states as props
                                    localSelectedLocations={pieLocalSelectedLocations}
                                    setLocalSelectedLocations={setPieLocalSelectedLocations}
                                    pie_chartType={pie_chartType}
                                    setPie_chartType={setPie_chartType}
                                    pieIndicators={pieIndicators}
                                    setPieIndicators={setPieIndicators}
                                />
                            )}

                            {selectedVisualizations?.value === "stacked" && (
                                <StackedVisualization
                                    indicators={reduxIndicators}
                                    locations={selectedMapFeatures}
                                    onSubmit={(data) => handleCentralizedSubmit("stacked", data)}
                                    // Pass persistent states as props
                                    localSelectedLocations={stackedLocalSelectedLocations}
                                    setLocalSelectedLocations={setStackedLocalSelectedLocations}
                                    stacked_chartType={stacked_chartType}
                                    setStacked_chartType={setStacked_chartType}
                                    stackedIndicators={stackedIndicators}
                                    setStackedIndicators={setStackedIndicators}
                                />
                            )}

                            {selectedVisualizations?.value === "table" && (
                                <TableVisualization
                                    indicators={reduxIndicators}
                                    locations={selectedMapFeatures}
                                    onSubmit={(data) => handleCentralizedSubmit("table", data)}
                                    // Pass persistent states as props
                                    localSelectedLocations={tableLocalSelectedLocations}
                                    setLocalSelectedLocations={setTableLocalSelectedLocations}
                                    selectedItems={tableSelectedItems}
                                    setSelectedItems={setTableSelectedItems}
                                />
                            )}
                        </div>

                        {/* Workspace Section - Always visible */}
                        <div className="col-12 mt-3">
                            <WorkspaceSection
                                indicators={reduxIndicators}
                                locations={selectedMapFeatures}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default MapConfig;